import type { RecordedMoney } from "./types";

const DECIMAL = /^-?[0-9]+(?:\.[0-9]+)?$/;
const CURRENCY = /^[A-Z]{3}$/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function moneyField(projection: unknown, field: string): RecordedMoney | undefined {
  const p = record(projection);
  if (p?.contract_version !== "console-recorded-money.v1" || p.evidence_state !== "legacy_recorded_unverified") {
    return undefined;
  }
  const observation = record(p[field]);
  return observation ? observation as unknown as RecordedMoney : undefined;
}

/** Exact strings, no Number/parseFloat, FX, rounding, or fallback currency. */
export function formatRecordedMoney(value: unknown): string {
  const m = record(value);
  if (!m || m.evidence_state !== "legacy_recorded_unverified") return "Amount and currency unknown";
  const amountKnown = m.amount_state === "recorded" && typeof m.amount === "string"
    && m.amount.trim() === m.amount && DECIMAL.test(m.amount);
  const currencyKnown = m.currency_state === "recorded" && typeof m.currency === "string"
    && m.currency.length === 3 && CURRENCY.test(m.currency);
  if (!amountKnown) {
    const label = m.amount_state === "invalid" ? "Amount invalid" : "Amount unknown";
    return currencyKnown ? `${label} (${m.currency})` : `${label}; currency unknown`;
  }
  // The full recorded decimal is shown, including a recorded zero. Currency
  // symbols and JS floating-point formatting would hide units or precision.
  return currencyKnown ? `${m.currency} ${m.amount}` : `${m.amount} (currency unknown)`;
}

/**
 * ANA-01 reconciled rollups (`/console/reconciled/*`) carry integer minor
 * units + a pinned exponent, never a decimal amount — this renders that
 * exactly, with no Number()/float conversion.
 */
export function formatReconciledMinor(amountMinor: string, exponent: number, currency: string): string {
  if (!/^-?[0-9]+$/.test(amountMinor) || !Number.isInteger(exponent) || exponent < 0) {
    return "Amount unavailable";
  }
  const negative = amountMinor.startsWith("-");
  const digits = negative ? amountMinor.slice(1) : amountMinor;
  const padded = digits.padStart(exponent + 1, "0");
  const whole = padded.slice(0, padded.length - exponent) || "0";
  const frac = exponent > 0 ? "." + padded.slice(padded.length - exponent) : "";
  return `${negative ? "-" : ""}${currency} ${whole}${frac}`;
}

export function projectionAsOf(value: unknown): string {
  if (typeof value !== "string") return "as-of unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "as-of unavailable" : `as of ${date.toLocaleString()}`;
}
